/**
 * Dashboard Hook
 * Manages dashboard data fetching and widget data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboardsApi, widgetsApi } from '../api/client';
import { Dashboard, Widget, WidgetData } from '../types';
import { useAppSettings } from '../contexts';

interface UseDashboardResult {
  dashboard: Dashboard | null;
  isLoading: boolean;
  error: string | null;
  widgetData: Record<string, WidgetData>;
  widgetLoading: Record<string, boolean>;
  widgetErrors: Record<string, string>;
  refreshWidget: (widgetId: string) => void;
  refreshAllWidgets: () => void;
  refetchDashboard: () => void;
}

export function useDashboard(dashboardId: string | null): UseDashboardResult {
  const queryClient = useQueryClient();
  const { refreshInterval: defaultRefreshInterval } = useAppSettings();
  const [widgetData, setWidgetData] = useState<Record<string, WidgetData>>({});
  const [widgetLoading, setWidgetLoading] = useState<Record<string, boolean>>({});
  const [widgetErrors, setWidgetErrors] = useState<Record<string, string>>({});
  const refreshIntervalRef = useRef<number | null>(null);

  // Fetch dashboard
  const {
    data: dashboard,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dashboard', dashboardId],
    queryFn: () => dashboardsApi.get(dashboardId!),
    enabled: dashboardId !== null,
  });

  // Refetch dashboard (useful after updates)
  const refetchDashboard = useCallback(() => {
    refetch();
  }, [refetch]);

  // Fetch data for a single widget
  const fetchWidgetData = useCallback(async (widget: Widget) => {
    setWidgetLoading((prev) => ({ ...prev, [widget.id]: true }));
    setWidgetErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[widget.id];
      return newErrors;
    });

    try {
      const data = await widgetsApi.getData(widget.id);
      setWidgetData((prev) => ({ ...prev, [widget.id]: data }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load widget data';
      setWidgetErrors((prev) => ({ ...prev, [widget.id]: message }));
    } finally {
      setWidgetLoading((prev) => ({ ...prev, [widget.id]: false }));
    }
  }, []);

  // Refresh a single widget
  const refreshWidget = useCallback(
    (widgetId: string) => {
      const widget = dashboard?.widgets.find((w) => w.id === widgetId);
      if (widget) {
        fetchWidgetData(widget);
      }
    },
    [dashboard, fetchWidgetData]
  );

  // Refresh all widgets
  const refreshAllWidgets = useCallback(() => {
    if (dashboard?.widgets) {
      dashboard.widgets.forEach((widget) => {
        fetchWidgetData(widget);
      });
    }
  }, [dashboard, fetchWidgetData]);

  // Fetch widget data when dashboard loads
  useEffect(() => {
    if (dashboard?.widgets) {
      dashboard.widgets.forEach((widget) => {
        fetchWidgetData(widget);
      });
    }
  }, [dashboard, fetchWidgetData]);

  // Set up auto-refresh — uses dashboard-specific interval or app-wide default
  useEffect(() => {
    const intervalSec = dashboard?.auto_refresh
      ? (dashboard.refresh_interval_seconds || defaultRefreshInterval)
      : 0;

    if (intervalSec > 0) {
      const intervalMs = intervalSec * 1000;
      refreshIntervalRef.current = window.setInterval(() => {
        refreshAllWidgets();
      }, intervalMs);
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [dashboard, refreshAllWidgets, defaultRefreshInterval]);

  return {
    dashboard: dashboard || null,
    isLoading,
    error: error instanceof Error ? error.message : null,
    widgetData,
    widgetLoading,
    widgetErrors,
    refreshWidget,
    refreshAllWidgets,
    refetchDashboard,
  };
}
